import { useQuery } from "react-query";
import { getLimits } from "api/limits";

interface AllLimits {
  free: {
    questionChatMessages: number;
    questions: number;
  };
  paid: {
    questionChatMessages: number;
    questions: number | null;
  };
  trial: {
    questionChatMessages: number;
    questions: number;
  };
}

interface PlanLimits {
  questionChatMessages: number;
  questions: number | null;
}

interface UsedLimits {
  questionChatMessages: number;
  questions: number;
}

interface UseChatLimitsReturn {
  isLoading: boolean;
  error: any;
  allLimits: AllLimits | null;
  userLimits: PlanLimits | null;
  usedLimits: UsedLimits | null;
  userPlan: string | null;
}

export function useChatLimits(): UseChatLimitsReturn {
  const { data, error, isLoading } = useQuery(
    "limits",
    async () => {
      const response = await getLimits();
      return response.body.data;
    },
    {
      staleTime: 60 * 60 * 1000, // 1 hour
      retry: 1,
      refetchOnWindowFocus: false
    }
  );

  if (!data) {
    return {
      isLoading,
      error,
      allLimits: null,
      userLimits: null,
      usedLimits: null,
      userPlan: null
    };
  }

  const allLimits: AllLimits = {
    free: {
      questionChatMessages: data.dailyLimits.free.questionChatMessages,
      questions: data.dailyLimits.free.questions
    },
    paid: {
      questionChatMessages: data.dailyLimits.paid.questionChatMessages,
      questions: data.dailyLimits.paid.questions
    },
    trial: {
      questionChatMessages: data.dailyLimits.trial.questionChatMessages,
      questions: data.dailyLimits.trial.questions
    }
  };

  const userPlan = data.plan;
  const userLimits = userPlan
    ? {
        questionChatMessages: data.dailyLimits[userPlan].questionChatMessages,
        questions: data.dailyLimits[userPlan].questions
      }
    : null;

  const usedLimits = data.usedLimits
    ? {
        questionChatMessages: data.usedLimits.questionChatMessages,
        questions: data.usedLimits.questions
      }
    : null;

  return {
    isLoading,
    error,
    allLimits,
    userLimits,
    usedLimits,
    userPlan
  };
}
